import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import toId from "@/utils/toId.utils";
import { FieldPath, FieldValues, useFormContext } from "react-hook-form";
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from "../../ui/multi-select";

export interface SimpleOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface MultiSelectFieldProps<T extends FieldValues = FieldValues> {
  name: FieldPath<T>;
  options: SimpleOption[];
  label?: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  disabled?: boolean;
  className?: {
    itemClass?: string;
    selectClass?: string;
    labelClass?: string;
    descriptionClass?: string;
    controlClass?: string;
    messageClass?: string;
  };
}

export default function MultiSelectField<T extends FieldValues = FieldValues>(
  props: MultiSelectFieldProps<T>
) {
  const { control } = useFormContext<T>();
  const {
    name,
    options,
    label,
    placeholder,
    description,
    required = false,
    disabled = false,
    className,
  } = props;

  const {
    itemClass,
    labelClass,
    selectClass,
    descriptionClass,
    controlClass,
    messageClass,
  } = className ?? {};

  const safeId = toId(name);
  const errorId = `${safeId}-error`;
  const descriptionId = description ? `${name}-description` : undefined;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const ariaDescribedBy = [
          descriptionId,
          fieldState.error ? errorId : undefined,
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <FormItem className={cn("space-y-0", itemClass)}>
            <FormLabel
              className={cn(
                "flex items-center justify-start text-sm font-medium",
                label ? "" : "hidden",
                labelClass
              )}
              htmlFor={safeId}
            >
              <span>{label || name}</span>
              {required && <span className="text-red-500">*</span>}
            </FormLabel>

            <MultiSelect
              values={field.value || []}
              onValuesChange={field.onChange}
            >
              <FormControl className={cn("", controlClass)}>
                <MultiSelectTrigger
                  id={safeId}
                  className={cn("w-full", selectClass)}
                  disabled={disabled}
                  aria-describedby={ariaDescribedBy}
                  aria-invalid={fieldState.error ? "true" : "false"}
                  aria-required={required}
                  onBlur={field.onBlur}
                >
                  <MultiSelectValue
                    placeholder={placeholder || `Select ${label || name}`}
                  />
                </MultiSelectTrigger>
              </FormControl>
              <MultiSelectContent search={false}>
                <MultiSelectGroup>
                  {options.map((option) => (
                    <MultiSelectItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {option.label}
                    </MultiSelectItem>
                  ))}
                </MultiSelectGroup>
              </MultiSelectContent>
            </MultiSelect>

            {description && (
              <FormDescription
                id={descriptionId}
                className={cn("text-sm text-gray-600", descriptionClass)}
              >
                {description}
              </FormDescription>
            )}
            <FormMessage className={cn("", messageClass)} id={errorId} />
          </FormItem>
        );
      }}
    />
  );
}
